import React, { useState, useCallback } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { Layout } from './components/Layout'
import { Dashboard } from './components/Dashboard'
import { SessionsList } from './components/SessionsList'
import { PresencesList } from './components/PresencesList'
import { NavigationGuardProvider, useNavigationGuard } from './context/NavigationGuardContext'

// Admin components
import { SessionsManagement } from './components/admin/SessionsManagement'
import { RegistrationsManagement } from './components/admin/RegistrationsManagement'
import { KidsManagement } from './components/admin/KidsManagement'
import { KidsLanding } from './components/admin/KidsLanding'
import { KidsEdit } from './components/admin/KidsEdit'
import { CentersManagement } from './components/admin/CentersManagement'
import { StagesManagement } from './components/admin/StagesManagement'
import { ParentsManagement } from './components/admin/ParentsManagement'
import { CommunicationManagement } from './components/admin/CommunicationManagement'
import { GroupsManagement } from './components/admin/GroupsManagement'
import { PublicHolidaysManagement } from './components/admin/PublicHolidaysManagement'
import { ReportsManagement } from './components/admin/ReportsManagement'
import { BillingManagement } from './components/admin/BillingManagement'
import { UsersManagement } from './components/admin/UsersManagement'

// Coordinator components
import { CenterPresencesManagement } from './components/coordinator/CenterPresencesManagement'
import { PresencesDuCentre } from './components/coordinator/PresencesDuCentre'
import { PointageGarderie } from './components/coordinator/PointageGarderie'
import { CenterKidsManagement } from './components/coordinator/CenterKidsManagement'
import { InclusionManagement } from './components/InclusionManagement'

export interface NavigationParams {
  centerId?: string
  year?: string
  periode?: string
  semaine?: string
  /** La garderie : `AAAA-MM-JJ` et le code de la plage à ouvrir (tableau de bord). */
  date?: string
  plage?: string
}

function StaffApp() {
  const { user, userProfile, loading } = useAuth()

  // La navigation de cet espace est geree par etat, pas par des routes. Tous les
  // roles entrent par le tableau de bord, qui s'adapte deja au role via le menu
  // lateral construit dans Layout.
  const [currentPage, setCurrentPage] = useState('dashboard')
  const [navParams, setNavParams] = useState<NavigationParams | null>(null)

  const navigateWithParams = useCallback((page: string, params?: NavigationParams) => {
    setNavParams(params || null)
    setCurrentPage(page)
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // L'authentification et le controle de role sont assures en amont par
  // StaffRoute. Ces deux gardes restent comme filet de securite : l'espace
  // staff n'affiche plus son propre formulaire de connexion, on renvoie vers
  // le login unique de l'application.
  if (!user || !userProfile) {
    return <Navigate to="/login" replace />
  }

  const allowedRoles = ['admin', 'anim', 'coordi']
  if (!allowedRoles.includes(userProfile.role || '')) {
    return <Navigate to="/" replace />
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onNavigate={navigateWithParams} />
      
      // Admin pages
      case 'sessions':
        return <SessionsManagement />
      case 'registrations':
        return <RegistrationsManagement />
      case 'kids':
        return <KidsLanding isAdmin={userProfile.role === 'admin'} onNavigate={(p) => setCurrentPage(p)} />
      case 'kids-view':
        return <KidsManagement initialParams={navParams} onParamsConsumed={() => setNavParams(null)} peutModifier={userProfile.role === 'admin'} />
      case 'kids-edit':
        return <KidsEdit onBack={() => setCurrentPage('kids')} />
      case 'centers':
        return <CentersManagement />
      case 'parents':
        return <ParentsManagement />
      case 'stages':
        return <StagesManagement />
      case 'communication':
        return <CommunicationManagement />
      case 'groups':
        return <GroupsManagement onNavigatePage={setCurrentPage} />
      case 'public-holidays':
        return <PublicHolidaysManagement />
      // Les écrans des animateurs, pour tout le centre (25/10/2026) ; l'ancien tableau reste joignable.
      case 'admin-presences':
      case 'center-presences':
        return (
          <PresencesDuCentre
            initialParams={navParams}
            onParamsConsumed={() => setNavParams(null)}
            onAncienEcran={() => setCurrentPage('presences-ancien')}
            onPointerLaGarderie={(p) => navigateWithParams('garderie-pointage', p)}
          />
        )
      // La liste du jour d'une école de garderie, en un geste depuis le tableau de bord (02/11/2026).
      case 'garderie-pointage':
        return userProfile.role === 'admin' || userProfile.role === 'coordi'
          ? <PointageGarderie initialParams={navParams} onRetour={() => navigateWithParams('dashboard')} />
          : <div className="text-center py-12 text-gray-500">Acces non autorise</div>
      case 'presences-ancien':
        return <CenterPresencesManagement initialParams={navParams} onParamsConsumed={() => setNavParams(null)} />
      
      // Animator pages
      case 'presences':
        return <PresencesList />
      case 'my-sessions':
        return <SessionsList />
      case 'kids-info':
        return <div className="text-center py-12 text-gray-500">Informations enfants - À développer</div>
      
      // Coordinator pages
      case 'center-sessions':
        return <div className="text-center py-12 text-gray-500">Sessions du centre - À développer</div>
      case 'center-groups':
        return <GroupsManagement onNavigatePage={setCurrentPage} />
      case 'center-kids':
        return <CenterKidsManagement initialParams={navParams} onParamsConsumed={() => setNavParams(null)} />
      case 'center-reports':
        return <div className="text-center py-12 text-gray-500">Acces non autorise</div>
      
      // Common pages to develop
      case 'waiting-list':
        return <div className="text-center py-12 text-gray-500">Liste d'attente - À développer</div>
      case 'cancellations':
        return <div className="text-center py-12 text-gray-500">Gestion des annulations - À développer</div>
      case 'inclusion':
        // Les demandes se traitent dans l'administration ; ici, les fiches et les niveaux.
        return userProfile.role === 'admin' || userProfile.role === 'coordi'
          ? <InclusionManagement />
          : <div className="text-center py-12 text-gray-500">Acces non autorise</div>
      case 'reports':
        return <ReportsManagement />
      case 'billing':
        return <BillingManagement />
      case 'users':
        return userProfile.role === 'admin'
          ? <UsersManagement />
          : <div className="text-center py-12 text-gray-500">Acces non autorise</div>
      case 'settings':
        return <div className="text-center py-12 text-gray-500">Paramètres - À développer</div>
      
      default:
        return <Dashboard onNavigate={navigateWithParams} />
    }
  }

  return (
    <NavigationGuardProvider>
      <AppShell currentPage={currentPage} setCurrentPage={setCurrentPage}>
        {renderCurrentPage()}
      </AppShell>
    </NavigationGuardProvider>
  )
}

function AppShell({
  currentPage,
  setCurrentPage,
  children,
}: {
  currentPage: string
  setCurrentPage: (page: string) => void
  children: React.ReactNode
}) {
  const { requestNavigation } = useNavigationGuard()
  const handleNavigate = (page: string) => {
    if (page === currentPage) return
    requestNavigation(() => setCurrentPage(page))
  }
  return (
    <Layout currentPage={currentPage} onNavigate={handleNavigate}>
      {children}
    </Layout>
  )
}

export default StaffApp